import {Flex, Icon, Text, Theme as ThemeWithoutType, Title} from 'exsportia-components'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {useStoreOrder} from '../../api/store'
import {CreatedBookingsModal} from '../../components/created-bookings-modal/created-bookings-modal'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {BookingService} from '../../services/booking'
import {useStoreCart} from '../../services/store/cart'
import {ThemeType} from '../../theme/types'
import {Button} from '../../ui/components/button/button'
import {CHECKIN_URL} from '../../utils/environment'
import {useLocale} from '../../utils/i18n'
import {getPosPaymentInfoFromSession} from '../../utils/local-storage'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {useTimeout} from '../../utils/use-idle-timer'
import {extractDataFromQuery} from './payment-page'

export const PaymentSuccess: FC = () => {
  const {l} = useLocale()
  const [isFromCheckIn, setIsFromCheckIn] = useState(false)
  const navigate = useNavigate()
  const Theme = ThemeWithoutType as ThemeType
  const params = useParams<{venueId: string}>()
  const storeCart = useStoreCart(params.venueId)
  const [searchParams] = useSearchParams()
  const queryData = useMemo(() => extractDataFromQuery(searchParams), [searchParams])
  const {openModal, closeAllModals} = useModalActions()
  const isBookingEntity = queryData.entity === 'booking' || queryData.entity === 'booking_update'
  const isSaleEntity = queryData.entity === 'sale'
  const {isLoggedIn} = useAuthState(params.venueId)
  // A VMS payment link is paid by whoever holds the link — often with no account at all, and never
  // as the owner of a sale the venue itself created. GET store/orders/:saleId is signed-in and
  // owner-only, so handing that customer to the order page showed them "we could not find this
  // order" in place of the receipt they had just paid for (production, 16 Sep: 135 link payments
  // since 17 Aug belong to no customer account). Ask for the order first — its own retry covers the
  // webhook lag the redirect used to race — and only hand them over once it is theirs to read.
  const {data: storeOrder} = useStoreOrder(isSaleEntity && isLoggedIn ? queryData.entityId || undefined : undefined)
  // After an external 3DS redirect the in-memory session data is gone, so rebuild the list of
  // created bookings from the server. GET bookings/:id returns the whole group as an array when
  // given the group/draft id (entityId). WBA-4103.
  const {data: bookingData} = useQuery(
    BookingService.queries.getBookingQuery({
      params: {id: queryData.entityId},
      query: {entity: queryData.entity, entityId: queryData.entityId},
      enabled: isBookingEntity && !!queryData.entityId,
      refetchOnWindowFocus: false,
    }),
  )
  const groupBookings = useMemo(() => {
    const raw = bookingData as unknown
    return Array.isArray(raw) ? (raw as Record<string, string>[]) : null
  }, [bookingData])
  const isMultiBooking = !!groupBookings && groupBookings.length > 1
  const openBookingsModal = useCallback(() => {
    if (!groupBookings) {
      return
    }
    openModal({
      title: l('bookings'),
      footerConfig: {disabled: true},
      component: (
        <CreatedBookingsModal
          bookings={groupBookings.map((b) => ({
            bookingId: b.uid,
            title: b.activityName || b.name,
            subtitle: b.date,
          }))}
          onSelect={(id) => {
            closeAllModals()
            navigate(`/${params.venueId}/bookings/${id}`)
          }}
        />
      ),
    })
  }, [groupBookings, openModal, closeAllModals, navigate, params.venueId, l])
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (isMultiBooking && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true
      openBookingsModal()
    }
  }, [isMultiBooking, openBookingsModal])
  const handleGoToCheckInApp = () => {
    window.location.href = `${CHECKIN_URL}`
  }

  const {goBackMessage} = useTimeout({
    delay: 3000,
    isDisabled: !isFromCheckIn,
    // Card is paid on the kiosk itself, so the screen is being touched right up
    // to the success page. Idle-reset would restart the countdown on every tap
    // and make the card return arrive far later than the PromptPay one.
    resetOnActivity: false,
    callback: () => {
      handleGoToCheckInApp()
      return null
    },
  })
  useEffect(() => {
    const {entityId, withQR, posTerminalProcessId} = getPosPaymentInfoFromSession()
    if (entityId === queryData.entityId && !withQR && isNotNilAndNotEmpty(posTerminalProcessId)) {
        setIsFromCheckIn(true)
    }
    if (queryData.posTerminalProcessId && !queryData.withQR) {
      setIsFromCheckIn(true)
    }
  }, [queryData, params.venueId])
  // A paid store order has its own page with next-step instructions — send the
  // customer straight there instead of showing booking-flavoured "close this
  // tab" copy (Dima 2026-08-17).
  useEffect(() => {
    if (!isSaleEntity || !queryData.entityId || isFromCheckIn) {
      return
    }
    // WBA-4570: the basket is kept alive through checkout so an abandoned payment does not lose
    // it. This is the point where the money actually arrived, so it is the point to empty it —
    // whether or not this customer can be shown the order afterwards.
    storeCart.clear()
    if (storeOrder) {
      navigate(`/${params.venueId}/orders/${queryData.entityId}`, {replace: true})
    }
    // storeCart is intentionally not a dependency: it changes on every basket mutation, and this
    // effect must fire on the payment result, not on the cart.
  }, [isSaleEntity, queryData.entityId, isFromCheckIn, storeOrder, navigate, params.venueId])

  const handleClickGoToEntity = () => {
    if (!queryData.entity || !queryData.entityId) {
      return
    }
    if (isSaleEntity) {
      // Same rule as the redirect: the order page is only reachable by the account that owns it.
      if (storeOrder) {
        navigate(`/${params.venueId}/orders/${queryData.entityId}`)
      }
      return
    }
    if (isBookingEntity) {
      // Multi-session group → list all created bookings instead of opening just one.
      if (isMultiBooking) {
        openBookingsModal()
        return
      }
      const redirectPath = `/${params.venueId}/bookings/${queryData.entityId}`
      navigate(redirectPath)
      return
    }

    if (queryData.entity === 'subscription') {
      const redirectPath = `/${params.venueId}/subscription-bookings/${queryData.entityId}/profile`
      navigate(redirectPath)
      return
    }

    const redirectPath = `/clear-view/${params.venueId}/payment?entityId=${queryData.entityId}&entity=${queryData.entity}`
    navigate(redirectPath)
  }
  const handleClickGoToVenue = () => {
    navigate(`/${params.venueId}`)
  }

  return (
    <GridLayout
      header={
        <Flex
          p='8px'
          justifyContent='center'
        >
          <Title
            settings={{textAlign: 'center'}}
            text={l('paymentSuccessful')}
          />
        </Flex>
      }
    >
      <Flex
        alignItems='center'
        gap={Theme.spaces.multiplier2x}
        flexDirection='column'
      >
        <Icon
          settings={{
            color: '#0DC268',
            width: '120px',
            height: '120px',
            iconWidth: '120px',
            iconHeight: '120px',
          }}
          icon={'doneIllustration'}
        />
        {
          isFromCheckIn
          ? (
              <>
                <Flex>
                  {goBackMessage}
                </Flex>
                <Button
                  onClick={handleGoToCheckInApp}
                >
                  {l('goBack')}
                </Button>
              </>
            )
            : (
              <>
                <Text
                  settings={{textAlign: 'center'}}
                  text={l('you_can_close_tab', {caseAction: 'capitalize'})}
                />
                {queryData.entityId && queryData.entity && (!isSaleEntity || !!storeOrder) && (
                  <Button onClick={handleClickGoToEntity}>{l('viewDetails')}</Button>
                )}
                {params.venueId && (
                  <Button
                    type='secondary'
                    onClick={handleClickGoToVenue}
                  >
                    {l('backToVenue')}
                  </Button>
                )}
              </>
            )
        }
      </Flex>
    </GridLayout>
  )
}
